import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ArrowUpOnSquareIcon from "@heroicons/react/24/solid/ArrowUpOnSquareIcon";
import {
  Box,
  Button,
  Card,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { CustomersSearch } from "@/sections/customer/customers-search";
import { useGetSuggestedSearchesQuery } from "@/app/features/suggestedSearch/suggestedSearchApiSlice";
import SuggestedSearchListing from "./components/SuggestedSearchListing";
import SuggestedSearchDeleteModal from "./components/SuggestedSearchDeleteModal";

const TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "global", label: "Global search" },
  { value: "ai", label: "AI search (Find a Car)" },
];

export default function SuggestedSearchPage() {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const { data, isLoading, isFetching, error } = useGetSuggestedSearchesQuery({
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
    search,
    type: typeFilter === "all" ? undefined : typeFilter,
  });

  const [rowCountState, setRowCountState] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    setRowCountState((prev) =>
      data?.data?.total !== undefined ? data.data.total : prev
    );
  }, [data]);

  const handleSelectDelete = (item) => {
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  };

  return (
    <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
      {selectedItem && (
        <SuggestedSearchDeleteModal
          open={isDeleteModalOpen}
          handleClose={() => setIsDeleteModalOpen(false)}
          selectedItem={selectedItem}
        />
      )}

      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" spacing={4}>
            <Box>
              <Typography variant="h4">Suggested Searches</Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Two separate lists — Global search and AI search (Find a Car).
                Each type: min 3 recommended, max 6 active.
              </Typography>
            </Box>
            <Button
              color="inherit"
              startIcon={
                <SvgIcon fontSize="small">
                  <ArrowUpOnSquareIcon />
                </SvgIcon>
              }
              variant="contained"
              component={Link}
              to="/suggested-search/create"
            >
              Add
            </Button>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "stretch" }}
          >
            <Box sx={{ flex: 1, width: "100%" }}>
              <CustomersSearch search={search} setSearch={setSearch} />
            </Box>
            <Card
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                minWidth: { xs: "100%", sm: 260 },
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="suggested-search-type-filter" shrink>
                  Type
                </InputLabel>
                <Select
                  labelId="suggested-search-type-filter"
                  id="suggested-search-type-select"
                  label="Type"
                  value={typeFilter}
                  displayEmpty
                  notched
                  renderValue={(selected) => {
                    const option = TYPE_FILTER_OPTIONS.find(
                      (item) => item.value === selected
                    );
                    return option?.label || "All types";
                  }}
                  onChange={(event) => {
                    setTypeFilter(event.target.value);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                >
                  {TYPE_FILTER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Card>
          </Stack>

          <SuggestedSearchListing
            isLoading={isLoading || isFetching}
            error={error}
            data={data?.data}
            rowCountState={rowCountState}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            onSelectDelete={handleSelectDelete}
          />
        </Stack>
      </Container>
    </Box>
  );
}
